import React, { FC, useState } from 'react';
import moment from 'moment-timezone';
import cx from 'classnames';

// constants
import { SUPPORTED_LANGUAGES } from 'core/constants';

// components
import { MenuItem, IconButton, Tooltip } from '@material-ui/core';
import StyledMenu from '../../../../../Menu';

// icons
import { ReactComponent as DeleteSvg } from 'assets/image/trash-light.svg';
import { ReactComponent as ThreeDotsSvg } from 'assets/image/threeDots.svg';
import { ReactComponent as InfoIcon } from 'assets/image/info.svg';

// styles & types
import styles from './ImportedQuotesTable.module.scss';
import { UploadedQuote } from '../UploadQuotesInput';

interface IImportedQuotesProps {
	quotes: Array<UploadedQuote>;
	handleQuoteDelete: (quoteIndex: number) => void;
}

export const ImportedQuotesTable: FC<IImportedQuotesProps> = ({ quotes, handleQuoteDelete }) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [openItemIndex, setOpenItemIndex] = useState<number | null>(null);

	const handleActionsButtonClose = () => {
		setAnchorEl(null);
		setOpenItemIndex(null);
	};

	const handleDeleteClick = (quoteIndex: number) => {
		handleActionsButtonClose();
		handleQuoteDelete(quoteIndex);
	};

	const handleActionsButtonClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
		setAnchorEl(event.currentTarget);
		setOpenItemIndex(index);
	};

	return (
		<div className={styles.tableWrapper}>
			<table className={styles.quotesTable}>
				<thead>
					<tr>
						<th className={styles.quotesTableDate}>Date</th>
						{SUPPORTED_LANGUAGES.map(lang => (
							<th key={lang.key} className={styles.quotesTableName}>
								{lang.label}
								{lang.required ? '*' : ''}
							</th>
						))}
						<th aria-label="Actions" />
					</tr>
				</thead>
				<tbody>
					{quotes.map((quote, index) => {
						const isValidDate = Boolean(quote.startDate && moment(quote.startDate).isValid());
						return (
							<tr key={index}>
								<td
									className={styles.quotesTableDate}
									title={
										(!quote.errors?.startDate &&
											!quote.errors?.nonUniqueDate &&
											isValidDate &&
											moment(quote.startDate).format('DD MMM')) ||
										undefined
									}
								>
									<div>
										{quote.errors?.startDate ? (
											<Tooltip title={quote.errors.startDate}>
												<InfoIcon className={styles.warning} />
											</Tooltip>
										) : null}
										{!quote.errors?.startDate && quote.errors?.nonUniqueDate ? (
											<Tooltip title={quote.errors.nonUniqueDate}>
												<InfoIcon className={styles.warning} />
											</Tooltip>
										) : null}
										{isValidDate && <span>{moment(quote.startDate).format('DD/MM/YYYY')}</span>}
									</div>
								</td>
								{SUPPORTED_LANGUAGES.map(lang => {
									const text = quote.messages?.[lang.key] || '';
									const errorMsg = quote.errors?.[lang.key];
									return (
										<td
											key={lang.key}
											className={cx(styles.quotesTableName, {
												[styles.quotesTableName_rtl]: lang.dir === 'rtl' && Boolean(text),
											})}
											dir={lang.dir === 'rtl' && Boolean(text) ? 'rtl' : 'ltr'}
											title={text || undefined}
										>
											<div>
												{errorMsg ? (
													<Tooltip title={errorMsg}>
														<InfoIcon className={styles.warning} />
													</Tooltip>
												) : null}
												<span className={cx({ [styles.emptyText]: !text })}>{text || '—'}</span>
											</div>
										</td>
									);
								})}
								<td align="center">
									<IconButton
										aria-label="actions"
										aria-controls={openItemIndex === index ? 'long-menu' : undefined}
										onClick={event => handleActionsButtonClick(event, index)}
									>
										<ThreeDotsSvg />
									</IconButton>
									<StyledMenu
										open={openItemIndex === index}
										anchorEl={anchorEl}
										onClose={handleActionsButtonClose}
										anchorOrigin={{
											vertical: 'top',
											horizontal: 'right',
										}}
										transformOrigin={{
											vertical: 'top',
											horizontal: 'right',
										}}
									>
										<MenuItem className={styles.menuItem} onClick={() => handleDeleteClick(index)}>
											<div className={styles.actionsContainer}>
												<DeleteSvg
													style={{ width: '16px', height: '16px' }}
													title="Delete quote"
												/>
												<span className={styles.actionsText}>Delete</span>
											</div>
										</MenuItem>
									</StyledMenu>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};
